import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { SettingsService } from '../../services/settings.services';
import { CommonService } from 'src/app/shared/common.service';
import {
  ISmartRepliesSettings,
  SmartRepliesProductFormat,
} from '../../models/settings.models';

// ─────────────────────────────────────────────────────────────────────────────
// SmartRepliesSettingsComponent
// ─────────────────────────────────────────────────────────────────────────────
// Global AI reply settings (WB-6371 scope v3). Rendered as the "Smart Replies"
// tab of the Smart Inbox settings page. Scope v3 keeps two presentation rules:
// pricing sourcing and product representation (bullets/table), with a live
// sample-reply preview. The sign-off is driven by the mailbox signature and
// stock is hardcoded by Alta, so neither is configurable here. The form is
// locked until at least one mailbox is connected.
// ─────────────────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-smart-replies-settings',
  templateUrl: './smart-replies-settings.component.html',
  styleUrls: ['./smart-replies-settings.component.scss'],
})
export class SmartRepliesSettingsComponent implements OnInit, OnDestroy {
  @Input() hasAccounts = false;

  settings: ISmartRepliesSettings = {
    pricing_source: 'any',
    product_format: 'bullets',
  };

  loading = false;

  private baseline = '';
  private destroy$ = new Subject<void>();

  // Price sourcing is not offered here -- Settings › Orders answers that
  // question once, for every channel. The stored pricing_source field stays in
  // the payload because the engine's legacy map still reads it for tenants that
  // never touched that screen.

  constructor(
    private settingsService: SettingsService,
    private commonService: CommonService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadSettings();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get dirty(): boolean {
    return JSON.stringify(this.settings) !== this.baseline;
  }

  // ── Data ───────────────────────────────────────────────────────────────────
  loadSettings(): void {
    this.loading = true;
    this.settingsService
      .getSmartRepliesSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
          // Merge onto the defaults so a partial/empty envelope never blanks the
          // form (which would leave no radio selected and let save() PATCH {}).
          this.settings = { ...this.settings, ...(settings ?? {}) };
          this.baseline = JSON.stringify(this.settings);
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.commonService.showToast(this.translate.instant('settings.smart_replies_load_error'), 'ERROR');
        },
      });
  }

  // ── Form changes ─────────────────────────────────────────────────────────────
  setProductFormat(value: SmartRepliesProductFormat): void {
    this.settings = { ...this.settings, product_format: value };
  }

  // ── Save / discard ───────────────────────────────────────────────────────────
  save(): void {
    this.settingsService
      .updateSmartRepliesSettings(this.settings)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
          this.settings = { ...settings };
          this.baseline = JSON.stringify(this.settings);
          this.commonService.showToast(this.translate.instant('settings.smart_replies_saved'), 'SUCCESS');
        },
        error: () => {
          this.commonService.showToast(this.translate.instant('settings.smart_replies_save_error'), 'ERROR');
        },
      });
  }

  discard(): void {
    this.settings = JSON.parse(this.baseline);
  }
}
